import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  HeadsetMicOutlined,
  Language,
  LockOutlined,
  PersonOutline,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useEffect, useId, useRef, useState } from "react";import { useNavigate } from "react-router-dom";

import logo from "../../assets/Rodovias_azul.png";

import { authService } from "../../services/auth.service";
import { Loading } from "../../components/Loading";

import ARTWORK_PHOTO_URL from "../../assets/imagem_urbana_noturna.png";

const LoginArtwork = () => {
  const svgRef = useRef(null);
  const clipId = useId();

  const [size, setSize] = useState({
    width: 0,
    height: 0,
    cardWidth: 0,
    cardHeight: 0,
  });

  useEffect(() => {
    const svg = svgRef.current;
    const card = svg?.parentElement;

    if (!svg || !card) return;

    const updateSize = () => {
      const svgRect = svg.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();

      if (
        !svgRect.width ||
        !svgRect.height ||
        !cardRect.width ||
        !cardRect.height
      ) {
        return;
      }

      const next = {
        width: svgRect.width,
        height: svgRect.height,
        cardWidth: cardRect.width,
        cardHeight: cardRect.height,
      };

      setSize((previous) =>
        previous.width === next.width &&
        previous.height === next.height &&
        previous.cardWidth === next.cardWidth &&
        previous.cardHeight === next.cardHeight
          ? previous
          : next
      );
    };

    const observer = new ResizeObserver(updateSize);

    observer.observe(svg);
    observer.observe(card);
    updateSize();

    return () => observer.disconnect();
  }, []);

  const { width, height, cardWidth, cardHeight } = size;

  // Mantém a escala das faixas vinculada à largura do cartão.
  const scale = cardWidth / 540;
  const slope = 4 / 9;

  // O SVG e o cartão compartilham o mesmo centro.
  const cardX = (width - cardWidth) / 2;
  const cardY = (height - cardHeight) / 2;

  // Ponta do espaço triangular entre as faixas da esquerda.
  const leftTipX = cardX - 242.625 * scale;
  const leftTipY = cardY + 62.5 * scale;

  // Ancoragens das faixas à direita do cartão.
  const rightX = cardX + cardWidth;
  const orangeY = cardY + cardHeight * 0.21;
  const whiteY = cardY + cardHeight * 0.55;

  const bandThickness = 165 * scale;
  const rightRise = (width - rightX) * slope;

  // Abertura à esquerda: abaixo do laranja e acima do branco.
  const leftPhotoPoints = `
    0,${leftTipY - leftTipX * slope}
    ${leftTipX},${leftTipY}
    0,${leftTipY + leftTipX * slope}
  `;

  // Abertura à direita: entre as duas faixas e a borda do cartão.
  const rightPhotoPoints = `
    ${rightX},${orangeY + bandThickness}
    ${width},${orangeY + bandThickness - rightRise}
    ${width},${whiteY + rightRise}
    ${rightX},${whiteY}
  `;

  return (
    <Box
      ref={svgRef}
      component="svg"
      aria-hidden="true"
      focusable="false"
      sx={{
        position: "absolute",
        display: "block",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: "100vw",

        // Também cobre a composição quando o cartão exige rolagem.
        height: "max(100vh, calc(100% + 64px))",
        "@supports (height: 100dvh)": {
          height: "max(100dvh, calc(100% + 64px))",
        },

        maxWidth: "none",
        overflow: "hidden",
        pointerEvents: "none",
        userSelect: "none",
        zIndex: 0,
      }}
    >
      {width > 0 && cardWidth > 0 && (
        <>
          {/* Define apenas as regiões onde a foto pode aparecer. */}
          <defs>
            <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
              <polygon points={leftPhotoPoints} />
              <polygon points={rightPhotoPoints} />
            </clipPath>
          </defs>

          {/* Foto atrás das faixas, visível somente nos recortes. */}
          <image
            href={ARTWORK_PHOTO_URL}
            x="0"
            y="0"
            width={width}
            height={height}
            preserveAspectRatio="xMidYMid slice"
            clipPath={`url(#${clipId})`}
          />

          {/* Faixas da esquerda: mesmas formas e cores anteriores. */}
          <g transform={`translate(${cardX} ${cardY}) scale(${scale})`}>
            <polygon
              points="-12012,-5333.333 -57,-20 -242.625,62.5 -12012,-5168.333"
              fill="var(--goia-orange)"
            />

            <polygon
              points="-12012,5293.333 -57,-20 -12,0 -12,125 -12012,5458.333"
              fill="var(--goia-white)"
            />
          </g>

          {/* Faixa laranja: ancorada a 21% da altura do cartão. */}
          <g
            transform={`translate(${rightX} ${orangeY}) scale(${scale})`}
          >
            <polygon
              points="0,0 12000,-5333.333 12000,-5168.333 0,165"
              fill="var(--goia-orange)"
            />
          </g>

          {/* Faixa branca: ancorada a 55% da altura do cartão. */}
          <g
            transform={`translate(${rightX} ${whiteY}) scale(${scale})`}
          >
            <polygon
              points="0,0 12000,5333.333 12000,5498.333 0,165"
              fill="var(--goia-white)"
            />
          </g>
        </>
      )}
    </Box>
  );
};

const Login = () => {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [erroLogin, setErroLogin] = useState(0);
  const [token] = useState(localStorage.getItem("user"));
  const [hasLoading, setHasLoading] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleClickLoginButton = async () => {
    setHasLoading(true);
    setErroLogin(0);

    try {
      const res = await authService.Login(username, password);

      const data = {
        token: res.data.token,
        expiry: res.data.expiry,
        id: res.data.id,
        username,
      };

      await authService.setLoggedUser(data);
      authService.getLoggedUser();

      setPassword("");
      setUsername("");
      navigate("/");
    } catch (error) {
      setErroLogin(error?.response?.status ?? 500);
    } finally {
      setHasLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleClickLoginButton();
  };

  useEffect(() => {
    if (token !== null) {
      navigate("/");
    }
  }, [navigate, token]);

  const inputSx = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      height: 54,
      borderRadius: "8px",
      backgroundColor: "var(--goia-white)",
      color: "var(--goia-text)",
      "& fieldset": {
        borderColor: "var(--goia-border)",
      },
      "&:hover fieldset": {
        borderColor: "var(--goia-gray-light)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "var(--goia-focus-ring)",
        borderWidth: "1px",
      },
    },
    "& .MuiInputBase-input": {
      fontSize: "15px",
      paddingTop: "15px",
      paddingBottom: "15px",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "var(--goia-text-muted)",
      opacity: 1,
    },
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100vh",
        "@supports (height: 100dvh)": {
          height: "100dvh",
        },
        overflowX: "hidden",
        overflowY: "hidden",
        backgroundColor: "var(--goia-navy)",
      }}
    >
      {hasLoading && <Loading />}

      {/* A pagina cresce com o form. O excesso decorativo fica recortado. */}
      <Box
        sx={{
          position: "relative",
          isolation: "isolate",
          width: "100%",
          minHeight: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: { xs: 2, sm: 3 },
        }}
      >
        {/* Referencia compartilhada pelas faixas e pelo formulario. */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: "480px",
            flexShrink: 0,
          }}
        >
          <LoginArtwork />

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              boxSizing: "border-box",
              backgroundColor: "var(--goia-white)",
              borderRadius: "18px",
              boxShadow: "0 18px 45px rgba(3, 21, 49, 0.24)",
              px: { xs: 3, sm: 5 },
              pt: { xs: 3, sm: 2 },
              pb: { xs: 3, sm: 4 },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: { xs: 90, sm: 116 },
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Rodovias"
                sx={{
                  display: "block",
                  width: "auto",
                  maxWidth: { xs: "72%", sm: "68%" },
                  maxHeight: { xs: 82, sm: 105 },
                  objectFit: "contain",
                }}
              />
            </Box>

            <Box
              sx={{
                height: "1px",
                width: "100%",
                backgroundColor: "var(--goia-border)",
                mt: { xs: 2, sm: 2.5 },
                mb: { xs: 3, sm: 4 },
              }}
            />

            <Typography
              component="h1"
              sx={{
                color: "var(--goia-text)",
                fontSize: { xs: "25px", sm: "28px" },
                lineHeight: 1.2,
                fontWeight: 400,
                mb: 2.25,
              }}
            >
              Acesse sua conta
            </Typography>

            {/* Usuário */}
            <Typography
              component="label"
              htmlFor="login-username"
              sx={{
                display: "block",
                color: "var(--goia-text)",
                fontSize: "14px",
                fontWeight: 500,
                mb: 0.75,
              }}
            >
              Usuário
            </Typography>

            <TextField
              id="login-username"
              type="text"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="Seu usuário"
              autoComplete="username"
              variant="outlined"
              sx={{ ...inputSx, mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutline
                      sx={{
                        color: "var(--goia-text-muted)",
                        fontSize: 22,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            {/* Senha */}
            <Typography
              component="label"
              htmlFor="login-password"
              sx={{
                display: "block",
                color: "var(--goia-text)",
                fontSize: "14px",
                fontWeight: 500,
                mb: 0.75,
              }}
            >
              Senha
            </Typography>

            <TextField
              id="login-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Sua senha"
              autoComplete="current-password"
              variant="outlined"
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined
                      sx={{
                        color: "var(--goia-text-muted)",
                        fontSize: 22,
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPassword ? "Ocultar senha" : "Mostrar senha"
                      }
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ color: "var(--goia-text-muted)" }} />
                      ) : (
                        <Visibility sx={{ color: "var(--goia-text-muted)" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Lembrar-me / Esqueci minha senha */}
            <Box
              sx={{
                mt: 1.25,
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                gap: { xs: 1, sm: 2 },
              }}
            >
              <Box
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  color: "var(--goia-text)",
                  fontSize: "13px",
                  whiteSpace: "nowrap",
                }}
              >
                <Checkbox
                  size="small"
                  sx={{
                    p: 0,
                    mr: 0.7,
                    color: "var(--goia-text-muted)",
                    "&.Mui-checked": {
                      color: "var(--goia-orange)",
                    },
                  }}
                />
                Lembrar-me
              </Box>

              <Typography
                component="a"
                href="#"
                onClick={(event) => event.preventDefault()}
                sx={{
                  color: "var(--goia-orange)",
                  fontSize: "13px",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Esqueci minha senha
              </Typography>
            </Box>

            {/* Mensagem de erro */}
            {erroLogin !== 0 && (
              <Typography
                role="alert"
                sx={{
                  color: "var(--goia-danger)",
                  fontSize: "13px",
                  mt: 1.25,
                }}
              >
                {erroLogin === 400 || erroLogin === 401
                  ? "Usuário ou senha inválidos."
                  : "Ocorreu um erro inesperado. Tente novamente mais tarde."}
              </Typography>
            )}

            {/* Botão */}
            <Button
              type="submit"
              variant="contained"
              disableElevation
              sx={{
                width: "100%",
                height: 50,
                mt: 3.25,
                borderRadius: "7px",
                backgroundColor: "var(--goia-orange)",
                color: "var(--goia-text-on-brand)",
                fontSize: "19px",
                fontWeight: 400,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "var(--goia-orange-600)",
                },
              }}
            >
              Entrar
            </Button>

            {/* Separador "ou" */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                my: 3.3,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: "1px",
                  backgroundColor: "var(--goia-border)",
                }}
              />
              <Typography
                sx={{
                  color: "var(--goia-text-muted)",
                  fontSize: "13px",
                }}
              >
                ou
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: "1px",
                  backgroundColor: "var(--goia-border)",
                }}
              />
            </Box>

            {/* Rodapé */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: { xs: 1.5, sm: 2 },
                alignItems: "center",
              }}
            >
              <Box
                component="a"
                href="#"
                onClick={(event) => event.preventDefault()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", sm: "flex-start" },
                  gap: 1.2,
                  color: "var(--goia-text)",
                  textDecoration: "none",
                  fontSize: "17px",
                }}
              >
                <HeadsetMicOutlined sx={{ fontSize: 25 }} />
                Suporte
              </Box>

              <Box
                component="a"
                href="#"
                onClick={(event) => event.preventDefault()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", sm: "flex-end" },
                  gap: 1.2,
                  color: "var(--goia-text)",
                  textDecoration: "none",
                  fontSize: "17px",
                }}
              >
                <Language sx={{ fontSize: 23 }} />
                Site institucional
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
